import { h } from 'vue';

import {
  renderBoolean,
  renderMoney,
  translateEnum,
  translateNumberedEntity,
} from '@rygen/rygen-ui/i18n';
import { ColumnSpecification } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';
import { buildSearchRoute, formatDateRange } from '@/util';

import ClientAvatarAndName from '@/components/client/ClientAvatarAndName.vue';
import GLCodeInlineEdit from '@/components/glCode/GLCodeInlineEdit.vue';
import OrderChip from '@/components/order/OrderChip.vue';
import ShippingPointSearchResult from '@/components/search/ShippingPointSearchResult.vue';
import AssignedUsersSearchResult from '@/components/user/AssignedUsersSearchResult.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';

import { OrderStatus } from '../order/OrderStatus';

import {
  DESTINATION_LOCATION_GROUP_COLUMN,
  ORIGIN_LOCATION_GROUP_COLUMN,
} from './locationGroupColumn';
import { OrderColumnProps } from './OrderColumnProps';

const tagSearchRouteBuilder = (tag: string) => {
  return buildSearchRoute('orderList', { tags: [tag] });
};

const OWNING_CLIENT_COLUMN = new ColumnSpecification({
  field: 'client',
  header: i18n.global.t('clients.clients'),
  body: ({ data }: OrderColumnProps) => {
    return h(ClientAvatarAndName, {
      name: data.client?.name,
      logo: data.client?.branding?.avatar?.signedUrl,
      accentColor: data.client?.branding?.accentColor,
    });
  },
});

const STATUS_COLUMN = new ColumnSpecification({
  field: 'status',
  header: i18n.global.t('common.status'),
  body: ({ data }: OrderColumnProps) => {
    return h(OrderChip, {
      status: data.status as OrderStatus,
    });
  },
});

const TAG_COLUMN = new ColumnSpecification({
  field: 'tags',
  header: i18n.global.t('tags.tags', 2),
  class: 'tags-column',
  body: ({ data }: OrderColumnProps) => {
    return h(TagContainer, {
      class: 'h-auto',
      modelValue: data.tags ?? [],
      routeBuilder: tagSearchRouteBuilder,
      readonly: true,
      maxVisible: 2,
    });
  },
});

const ORIGIN_COLUMN = new ColumnSpecification({
  field: 'origin',
  header: i18n.global.t('orders.origin'),
  body: ({ data }: OrderColumnProps) => {
    return h(ShippingPointSearchResult, {
      shippingPoint: data.origin,
    });
  },
});

const DESTINATION_COLUMN = new ColumnSpecification({
  field: 'destination',
  header: i18n.global.t('orders.destination'),
  body: ({ data }: OrderColumnProps) => {
    return h(ShippingPointSearchResult, {
      shippingPoint: data.destination,
    });
  },
});

const REQUESTED_PICKUP_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedPickupDateRange',
  header: i18n.global.t('orders.requestedPickup'),
  body: ({ data }: OrderColumnProps) => {
    return formatDateRange(data.requestedPickupDateRange);
  },
});

const PICKUP_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'pickupAppointmentRequired',
  header: i18n.global.t('shippingPoints.pickupAppointmentRequired'),
  body: ({ data }: OrderColumnProps) =>
    renderBoolean(data.pickupAppointmentRequired ?? false),
});

const REQUESTED_DELIVERY_DATE_RANGE_COLUMN = new ColumnSpecification({
  field: 'requestedDeliveryDateRange',
  header: i18n.global.t('orders.requestedDelivery'),
  body: ({ data }: OrderColumnProps) => {
    return formatDateRange(data.requestedDeliveryDateRange);
  },
});

const DELIVERY_APPOINTMENT_REQUIRED_COLUMN = new ColumnSpecification({
  field: 'deliveryAppointmentRequired',
  header: i18n.global.t('shippingPoints.deliveryAppointmentRequired'),
  body: ({ data }: OrderColumnProps) =>
    renderBoolean(data.deliveryAppointmentRequired ?? false),
});

const WEIGHT_COLUMN = new ColumnSpecification({
  field: 'weight',
  header: i18n.global.t('lineItems.weight'),
  body: ({ data }: OrderColumnProps) => {
    return data.weight === undefined
      ? '-'
      : translateNumberedEntity(
          data.weight,
          `uoms.weight.abbreviations.${data.weightUom}`,
        );
  },
});

const HANDLING_UNIT_COUNT = new ColumnSpecification({
  field: 'handlingUnitCount',
  header: i18n.global.t('lineItems.handlingUnits'),
  body: ({ data }: OrderColumnProps) => {
    return data.handlingUnitCount === undefined
      ? '-'
      : `${data.handlingUnitCount} ${data.handlingUnitDescription}`;
  },
});

const PRODUCT_NUMBER_COLUMN = new ColumnSpecification({
  field: 'productNumber',
  header: i18n.global.t('lineItems.productNumber'),
  body: ({ data }: OrderColumnProps) => data.productNumber ?? '-',
});

const PRODUCT_DESCRIPTION_COLUMN = new ColumnSpecification({
  field: 'productDescription',
  header: i18n.global.t('lineItems.productDescription'),
  body: ({ data }: OrderColumnProps) => data.productDescription ?? '-',
});

const HAZMAT_COLUMN = new ColumnSpecification({
  field: 'hazmat',
  header: i18n.global.t('lineItems.hazmat'),
  body: ({ data }: OrderColumnProps) => renderBoolean(data.hazmat),
});

const GL_CODE_COLUMN = new ColumnSpecification({
  field: 'glCodes',
  header: i18n.global.t('glCodes.glCodes', 2),
  class: 'gl-codes-column',
  body: ({ data }: OrderColumnProps) => {
    const glCodes =
      data.glCodes?.map((g) =>
        h(GLCodeInlineEdit, {
          modelValue: g,
          editable: false,
        }),
      ) ?? [];
    return h('div', { class: 'flex flex-gap-1' }, glCodes);
  },
});

const DIRECTION_COLUMN = new ColumnSpecification({
  field: 'direction',
  header: i18n.global.t('shipments.directions'),
  body: ({ data }: OrderColumnProps) => {
    return data.direction ? translateEnum(data.direction, 'Direction') : '-';
  },
});

const EQUIPMENT_TYPE_COLUMN = new ColumnSpecification({
  field: 'equipmentType',
  header: i18n.global.t('carriers.equipmentTypes'),
  body: ({ data }: OrderColumnProps) => data.equipmentType?.name ?? '-',
});

const FREIGHT_ALLOWANCE_ENABLED_COLUMN = new ColumnSpecification({
  field: 'freightAllowanceEnabled',
  header: i18n.global.t('orders.freightAllowance.freightAllowanceEnabled'),
  body: ({ data }: OrderColumnProps) =>
    renderBoolean(data.freightAllowanceEnabled),
});

const FREIGHT_ALLOWANCE_AMOUNT_COLUMN = new ColumnSpecification({
  field: 'freightAllowance',
  header: i18n.global.t('orders.freightAllowance.freightAllowance'),
  body: ({ data }: OrderColumnProps) => {
    return renderMoney(data.freightAllowance);
  },
});

const SPOT_MARKET_SUMMARY_COLUMN = new ColumnSpecification({
  field: 'spotMarketRequestCount',
  header: i18n.global.t('carrierPortal.spotMarket.spotMarkets', 2),
  body: ({ data }: OrderColumnProps) => {
    const spotMarketCountText = i18n.global.t(
      'carrierPortal.spotMarket.countSpotMarketRequestsSent',
      { count: data.spotMarketRequestCount },
    );
    if (!data.spotMarketRequestCount) {
      return spotMarketCountText;
    }

    const requestEl = h('div', {}, spotMarketCountText);
    const acceptedEl = h(
      'div',
      { class: 'text-gray-darker' },
      i18n.global.t(
        'carrierPortal.spotMarket.countSpotMarketRequestsAccepted',
        {
          count: data.spotMarketAcceptedCount,
        },
      ),
    );
    return h('div', {}, [requestEl, acceptedEl]);
  },
});

const USER_ASSIGNMENT_COLUMN = new ColumnSpecification({
  field: 'assignedUsers',
  header: i18n.global.t('common.assignedParam', {
    param: i18n.global.t('users.users', 2),
  }),
  body: ({ data }: OrderColumnProps) =>
    h(AssignedUsersSearchResult, {
      id: 'assign-user-button',
      modelValue: data.assignedUsers || [],
      parentId: data.id,
      ownerId: data.owner?.id,
      type: 'order',
    }),
});

const CANCELLATION_REASON_COLUMN = new ColumnSpecification({
  field: 'cancellationReason',
  header: i18n.global.t('orders.cancellationReason'),
  body: ({ data }: OrderColumnProps) => data.cancellationReason ?? '-',
});

const REQUESTED_MODE_COLUMN = new ColumnSpecification({
  field: 'requestedMode',
  header: i18n.global.t('common.requestedMode'),
  body: ({ data }: OrderColumnProps) =>
    data.requestedMode ? translateEnum(data.requestedMode, 'Mode') : '-',
});

const SOURCE_COLUMN = new ColumnSpecification({
  field: 'source',
  header: i18n.global.t('common.source'),
  body: ({ data }: OrderColumnProps) => data.source ?? '-',
});

const SWC_COLUMN = new ColumnSpecification({
  field: 'swc',
  header: i18n.global.t('swc.shipWithoutContact'),
  body: ({ data }: OrderColumnProps) => renderBoolean(data.swc ?? false),
});

const CUSTOMER_ROUTED_COLUMN = new ColumnSpecification({
  field: 'customerRouted',
  header: i18n.global.t('common.customerRouted'),
  body: ({ data }: OrderColumnProps) =>
    renderBoolean(data.customerRouted ?? false),
});

const HOT_COLUMN = new ColumnSpecification({
  field: 'hot',
  header: i18n.global.t('common.hot'),
  body: ({ data }: OrderColumnProps) => renderBoolean(data.hot ?? false),
});

const MILEAGE_COLUMN = new ColumnSpecification({
  field: 'totalMileage',
  header: i18n.global.t('common.mileage'),
  body: ({ data }: OrderColumnProps): string =>
    data.totalMileage
      ? i18n.global.t('rateShop.mileage', {
          count: Math.round(data.totalMileage),
        })
      : '-',
});

/**
 * All possible order columns
 *
 * Order is significant. This array is used as the default set of columns.
 **/
export const ORDER_COLUMNS: ColumnSpecification[] = [
  OWNING_CLIENT_COLUMN,
  STATUS_COLUMN,
  USER_ASSIGNMENT_COLUMN,
  TAG_COLUMN,
  ORIGIN_COLUMN,
  DESTINATION_COLUMN,
  REQUESTED_PICKUP_DATE_RANGE_COLUMN,
  PICKUP_APPOINTMENT_REQUIRED_COLUMN,
  REQUESTED_DELIVERY_DATE_RANGE_COLUMN,
  DELIVERY_APPOINTMENT_REQUIRED_COLUMN,
  GL_CODE_COLUMN,
  WEIGHT_COLUMN,
  HANDLING_UNIT_COUNT,
  PRODUCT_NUMBER_COLUMN,
  PRODUCT_DESCRIPTION_COLUMN,
  HAZMAT_COLUMN,
  DIRECTION_COLUMN,
  EQUIPMENT_TYPE_COLUMN,
  FREIGHT_ALLOWANCE_AMOUNT_COLUMN,
  FREIGHT_ALLOWANCE_ENABLED_COLUMN,
  SPOT_MARKET_SUMMARY_COLUMN,
  CANCELLATION_REASON_COLUMN,
  REQUESTED_MODE_COLUMN,
  SOURCE_COLUMN,
  SWC_COLUMN,
  CUSTOMER_ROUTED_COLUMN,
  HOT_COLUMN,
  MILEAGE_COLUMN,
  ORIGIN_LOCATION_GROUP_COLUMN,
  DESTINATION_LOCATION_GROUP_COLUMN,
];

export const ORDER_COLUMNS_SORT_FIELDS = ORDER_COLUMNS.map((col) => col.field)
  .filter(notNull)
  .concat('id', '');
